import React, { useState, useRef, useEffect, useCallback } from 'react';
import Spinner from '../components/Spinner';
import AccountModal from '../components/AccountModal';
import TrialStatus from '../components/TrialStatus';
import SubscriptionModal from '../components/SubscriptionModal';
import TrialExpiredMessage from '../components/TrialExpiredMessage';
import ContactSupportModal from '../components/ContactSupportModal';
import WelcomeModal from '../components/WelcomeModal';
import AvatarModal from '../components/AvatarModal';
import { sendChatMessage, getConversations, getConversationMessages, deleteConversation } from '../services/chatService'; 
import { useAuth } from '../context/AuthContext';
import { auth, resendVerificationEmail } from '../services/userService';
import { useTrialStatus } from '../hooks/useTrialStatus';
// Import icons
import { FiMenu, FiX, FiPlus, FiMessageSquare, FiTrash2, FiUser, FiMail, FiInfo } from 'react-icons/fi'; 

// Define the default system prompt as a constant
const DEFAULT_SYSTEM_PROMPT = `Your name is Bela. You are a Russian spy.`;
// const DEFAULT_SYSTEM_PROMPT = `Your name is Bela. You are a Russian girlfriend, talking to your boyfriend.
// Your clothes are always clean and stylish, sexy but not too revealing. You're always flirty but hide it in public.
// When you're in a private place with your boyfriend, you show how much you like him and how horny you are.
// You speak in a casual, conversational tone, simple language, use slang. When things get sexually intense you talk dirty and explicit.
// Don't make your replies too lengthy. Don't explain things too much unless necessary.
// When you respond make it sound like a real conversation. Don't describe the your action or the surroundings from a third person perspective.
// `;

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Loading state for sending message
  const messagesEndRef = useRef(null);
  const { appLogout, firebaseUser, userInfo, userInfoLoading, justCompletedSignup, clearSignupFlag } = useAuth();

  // Calculate trial status
  const trialStatus = useTrialStatus(userInfo);

  // State for email verification
  const [isVerified, setIsVerified] = useState(firebaseUser?.emailVerified || false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState({ message: '', error: false, loading: false });

  // --- New State for Conversation History ---
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null); // null for new chat
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false); // Loading state for fetching messages
  // Set initial state based on window width (md breakpoint is 768px)
  const [isPanelOpen, setIsPanelOpen] = useState(() => window.innerWidth >= 768); 
  const [conversationError, setConversationError] = useState(''); // Error fetching conversations/messages
  const [isDeleting, setIsDeleting] = useState(null); // Track which conversation is being deleted
  // Use the constant for the initial state
  const [currentSystemPrompt, setCurrentSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT); 
  
  // Account modal state
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  
  // Subscription modal state
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false); 

  // Contact support modal state
  const [isContactSupportModalOpen, setIsContactSupportModalOpen] = useState(false); 

  // Beta explanation state
  const [showBetaExplanation, setShowBetaExplanation] = useState(false);

  // Welcome modal state
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [hasShownWelcomeModal, setHasShownWelcomeModal] = useState(false);

  // Avatar modal state
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  // --- Fetch Conversations ---
  const fetchConversations = useCallback(async () => {
    if (!firebaseUser || !isVerified) return; // Don't fetch if not logged in or verified
    setIsLoadingConversations(true);
    setConversationError('');
    try {
      const token = await firebaseUser.getIdToken();
      const data = await getConversations(token);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversationError('Failed to load conversations.');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [firebaseUser, isVerified]); // Dependencies

  // --- Fetch Messages for a Selected Conversation ---
  const fetchMessages = useCallback(async (convId) => {
    if (!firebaseUser || !convId) return;
    setIsLoadingMessages(true);
    setMessages([]); // Clear previous messages
    setConversationError('');
    try {
      const token = await firebaseUser.getIdToken();
      const data = await getConversationMessages(token, convId);
      // Map backend messages structure { content, role } to our state structure
      const formattedMessages = data.messages.map(msg => ({
          role: msg.role,
          content: msg.content
      }));
      setMessages(formattedMessages);
      // Optionally set the system prompt if provided by the backend for this conversation
      if (data.systemPrompt) {
        setCurrentSystemPrompt(data.systemPrompt);
      } else {
        // Reset to default using the constant
         setCurrentSystemPrompt(DEFAULT_SYSTEM_PROMPT);
      }
    } catch (error) {
      console.error(`Error fetching messages for ${convId}:`, error);
      setConversationError('Failed to load messages for this conversation.');
      // Optionally reset to new chat state on error
      // setCurrentConversationId(null);
      // setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [firebaseUser]); // Dependency

  // --- Effects ---

  // Effect to fetch conversations when verification status changes or user logs in
  useEffect(() => {
    if (isVerified && firebaseUser) {
      fetchConversations();
    } else {
      // Clear conversations if user becomes unverified or logs out
      setConversations([]);
      setCurrentConversationId(null);
      setMessages([]);
    }
  }, [isVerified, firebaseUser, fetchConversations]);

  // Update verification status when firebaseUser changes (e.g., after reload)
  useEffect(() => {
    setIsVerified(firebaseUser?.emailVerified || false);
  }, [firebaseUser]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isVerified) {
      scrollToBottom();
    }
  }, [messages, isVerified]); // Keep dependency on messages

  // Effect to handle window resize for panel visibility
  useEffect(() => {
    const handleResize = () => {
      // Close panel if window becomes narrow (< 768px)
      // Open panel if window becomes wide (>= 768px)
      setIsPanelOpen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to show welcome modal for new users
  useEffect(() => {
    // Show welcome modal if:
    // 1. User is verified
    // 2. User info is loaded and not loading
    // 3. Trial status is loaded (not loading)
    // 4. User is a trial user (new account)
    // 5. User has no conversations (first time)
    // 6. Modal hasn't been shown yet in this session
    // 7. User just completed signup (new flag to distinguish from login)
    const shouldShowWelcome = isVerified && 
                              userInfo && 
                              !userInfoLoading && 
                              !trialStatus.loading &&
                              trialStatus.isTrialUser && 
                              conversations.length === 0 && 
                              !hasShownWelcomeModal &&
                              justCompletedSignup;

    if (shouldShowWelcome) {
      setIsWelcomeModalOpen(true);
      setHasShownWelcomeModal(true); // Mark as shown so it won't show again
      clearSignupFlag(); // Clear the signup flag after showing the modal
    }
  }, [isVerified, userInfo, userInfoLoading, trialStatus, conversations, hasShownWelcomeModal, justCompletedSignup, clearSignupFlag]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // --- Handle Selecting a Conversation ---
  const handleSelectConversation = (convId) => {
    if (convId === currentConversationId) return; // Avoid reloading same conversation
    setCurrentConversationId(convId);
    fetchMessages(convId);
    // Close panel on mobile after selection
    if (window.innerWidth < 768) {
        setIsPanelOpen(false);
    }
  };

  // --- Handle Starting a New Chat ---
  const handleNewChat = () => {
    // Don't block if still loading user info
    if (userInfoLoading) return;
    
    // Check trial status and block if expired
    if (!trialStatus.canUseChat) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    setCurrentConversationId(null);
    setMessages([]);
    setInput('');
    // Reset to default system prompt using the constant
    setCurrentSystemPrompt(DEFAULT_SYSTEM_PROMPT);
    // Close panel on mobile after selection
    if (window.innerWidth < 768) {
        setIsPanelOpen(false);
    }
  };

  // --- Handle Deleting a Conversation ---
  const handleDeleteConversation = async (convIdToDelete, e) => {
    e.stopPropagation(); // Prevent the click from selecting the conversation

    // Optional: Confirmation dialog
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }

    setIsDeleting(convIdToDelete); // Indicate deletion is in progress for this item
    setConversationError(''); // Clear previous errors

    try {
      const token = await firebaseUser.getIdToken();
      await deleteConversation(token, convIdToDelete);

      // Remove conversation from state
      setConversations(prev => prev.filter(conv => conv.id !== convIdToDelete));

      // If the deleted conversation was the current one, reset the chat view
      if (currentConversationId === convIdToDelete) {
        handleNewChat(); // Reset to new chat state
      }

    } catch (error) {
      console.error('Error deleting conversation:', error);
      setConversationError(`Failed to delete conversation: ${error.message}`);
    } finally {
      setIsDeleting(null); // Reset deletion indicator
    }
  };

  // --- Updated Handle Send Message ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !firebaseUser || !isVerified) return;

    // Don't block if still loading user info
    if (userInfoLoading) return;

    // Check trial status and block if expired
    if (!trialStatus.canUseChat) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    const userMessage = { role: 'user', content: input };
    const currentMessages = [...messages, userMessage]; // Include user message for API call

    setMessages(currentMessages); // Optimistically update UI
    setInput('');
    setIsLoading(true); // Start loading spinner for AI response

    try {
      const token = await firebaseUser.getIdToken();
      // Pass currentConversationId (can be null) and token
      const responseData = await sendChatMessage(
        currentMessages, // Send full history including new user message
        currentSystemPrompt, // Use the potentially conversation-specific prompt
        currentConversationId,
        token
      );

      // Add AI response to the conversation - CORRECTED ACCESSOR
      setMessages(prevMessages => [
        ...prevMessages,
        { role: 'assistant', content: responseData.aiResponse.content } // Access content via aiResponse
      ]);

      // If it was a new chat, update the currentConversationId and refresh list
      if (currentConversationId === null && responseData.conversationId) {
        setCurrentConversationId(responseData.conversationId);
        fetchConversations(); // Refresh list to show the new conversation
      } else if (currentConversationId !== null) {
         // If it was an existing chat, refresh list to update timestamp order
         fetchConversations();
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message to conversation
      setMessages(prevMessages => [
        ...prevMessages,
        { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.' }
      ]);
      // Optionally revert optimistic update if needed, though showing error might be better UX
      // setMessages(messages); 
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Allow new line with Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleLogout = async () => {
    console.log('Chat: handleLogout called');
    await appLogout(); 
    // No navigation needed, ProtectedRoute handles it
  };

  const handleVerificationCheck = async () => {
    setIsCheckingVerification(true);
    setResendStatus({ message: '', error: false, loading: false }); 
    const currentUser = auth.currentUser; 
    if (!currentUser) {
      console.error("Verification Check: No current user found.");
      setIsCheckingVerification(false);
      return;
    }
    try {
      await currentUser.reload(); 
      const updatedIsVerified = auth.currentUser?.emailVerified; 
      console.log('Verification Check: User reloaded, emailVerified:', updatedIsVerified);
      setIsVerified(updatedIsVerified || false); // Update state based on refreshed data
      if (!updatedIsVerified) {
        setResendStatus({ message: 'Verification not detected yet. Please ensure you clicked the link.', error: true, loading: false });
      } else {
         setResendStatus({ message: 'Verification successful!', error: false, loading: false });
         // Optionally fetch conversations now if they weren't fetched before
         if (conversations.length === 0) {
             fetchConversations();
         }
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setResendStatus({ message: 'Error checking verification status.', error: true, loading: false });
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleResendVerification = async () => {
    setResendStatus({ message: '', error: false, loading: true });
    try {
      await resendVerificationEmail();
      setResendStatus({ message: 'Verification email sent! Please check your inbox (and spam folder).', error: false, loading: false });
    } catch (error) {
      console.error("Error resending verification email:", error);
      setResendStatus({ message: 'Failed to resend verification email. Please try again later.', error: true, loading: false });
    }
  };


  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    // Use flex layout for the entire screen
    <div className="h-screen bg-gray-100 flex flex-col md:flex-row">
       {/* Toggle Button for Mobile */}
       <button
         onClick={() => setIsPanelOpen(!isPanelOpen)}
         // Changed background, text, and hover colors
         className="md:hidden fixed top-3 left-3 z-30 p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md shadow-lg" 
         aria-label={isPanelOpen ? "Close menu" : "Open menu"}
       >
         {isPanelOpen ? <FiX size={20} /> : <FiMenu size={20} />}
       </button>

      {/* Left Panel (Conversation History) */}
      {/* Removed md: prefix from flex and flex-col */}
      <div className={`
        fixed top-0 left-0 h-full z-20 
        bg-gray-200 text-gray-900 
        w-64 md:w-72 lg:w-80 
        transform transition-transform duration-300 ease-in-out 
        ${isPanelOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:relative md:translate-x-0 flex flex-col md:h-screen flex-shrink-0 
      `}>
        {/* Panel Header */}
        <div className="pl-16 md:pl-3 pr-3 py-3 flex items-center justify-between border-b border-gray-300 flex-shrink-0"> 
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-800">History</h2>
          </div>
          <button
            onClick={handleNewChat}
            title="New Chat"
            className="p-2 rounded hover:bg-gray-300 text-gray-700 transition-colors" 
          >
            <FiPlus size={20} />
          </button>
        </div>
        {/* Conversation List Area (flex-1 makes this grow) */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {isLoadingConversations && (
            <div className="flex justify-center items-center h-full">
              <Spinner color="text-gray-700" /> {/* Changed spinner color */}
            </div>
          )}
          {!isLoadingConversations && conversationError && (
             <p className="text-red-600 text-sm p-2">{conversationError}</p> /* Adjusted error color */
          )}
          {!isLoadingConversations && !conversationError && conversations.length === 0 && isVerified && (
            <p className="text-gray-600 text-sm p-2 text-center">No past conversations yet.</p> /* Adjusted text color */
          )}
          {!isLoadingConversations && conversations.map((conv) => (
            // Wrap button and delete icon in a div for layout
            <div key={conv.id} className="relative group flex items-center"> 
              <button
                onClick={() => handleSelectConversation(conv.id)}
                className={`w-full text-left pl-3 pr-8 py-2 rounded flex items-center gap-2 text-sm truncate ${ // Added pr-8 for delete button space
                  currentConversationId === conv.id ? 'bg-bela-100 text-bela-800 font-medium' : 'text-gray-700 hover:bg-gray-300' 
                } transition-colors`}
                title={conv.title || 'Untitled Conversation'}
                disabled={isDeleting === conv.id} // Disable while deleting this item
              >
                <FiMessageSquare className="flex-shrink-0" size={16} />
                <span className="flex-grow truncate">{conv.title || 'Untitled Conversation'}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(conv.updatedAt)}</span> 
              </button>
              {/* Delete Button */}
              <button
                onClick={(e) => handleDeleteConversation(conv.id, e)}
                title="Delete Conversation"
                // Adjusted classes: Always visible if current, otherwise visible on group-hover/focus
                className={`absolute right-1 top-1/2 transform -translate-y-1/2 p-1 rounded text-gray-500 hover:text-red-600 hover:bg-gray-300 transition-opacity 
                  ${currentConversationId === conv.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} 
                  ${isDeleting === conv.id ? 'opacity-100 cursor-not-allowed' : ''}`}
                disabled={isDeleting === conv.id}
              >
                {isDeleting === conv.id ? <Spinner size="small" color="text-red-600"/> : <FiTrash2 size={14} />}
              </button>
            </div>
          ))}
        </div>
        
        {/* Beta Notice - Subtle text version */}
        <div className="mx-2 mb-3 text-center">
          <span 
            onClick={() => setShowBetaExplanation(!showBetaExplanation)}
            className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 transition-colors"
          >
            Beta Version
          </span>
          
          {showBetaExplanation && (
            <div className="mt-2 text-xs text-gray-500 leading-relaxed">
              This site is currently in beta. You may experience occasional bugs or 
              new features being added. We appreciate your feedback to help us improve!
            </div>
          )}
        </div>
        
        {/* Trial Status Component */}
        {isVerified && userInfoLoading && (
          <div className="mx-2 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-700">
              <Spinner size="small" />
              <span className="text-sm">Loading status...</span>
            </div>
          </div>
        )}
        {isVerified && !userInfoLoading && userInfo && (
          <TrialStatus 
            trialStatus={trialStatus}
            onUpgradeClick={() => setIsSubscriptionModalOpen(true)}
          />
        )}
        
         {/* Account and Logout Buttons at the bottom of the panel */}
         <div className="p-4 border-t border-gray-300 flex-shrink-0 space-y-2"> 
            <button
              onClick={() => setIsAccountModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 text-sm font-semibold py-2 px-3 rounded transition-colors" 
            >
              <FiUser size={16} />
              Account
            </button>
            <button
              onClick={() => setIsContactSupportModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 text-sm font-semibold py-2 px-3 rounded transition-colors"
            >
              <FiMail size={16} />
              Send Feedback
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-red-600 hover:text-red-800 text-sm font-semibold py-2 px-3 rounded transition-colors text-center" 
            >
              Logout
            </button>
         </div>
      </div>

      {/* Main Chat Area */}
      {/* This outer div remains a flex column */}
      <div className="flex-1 flex flex-col overflow-hidden md:h-screen"> 
        
        {/* Fixed Header */}
        <div className="bg-white shadow-md p-4 flex-shrink-0 z-10"> 
          <div className="max-w-4xl mx-auto w-full">
            <div className="flex items-center justify-center relative">
              <h1 className="font-['Dancing_Script'] text-2xl font-bold text-gray-900 text-center">
                The Bela Experience
              </h1>
              {/* AI Disclosure Info Icon */}
              <div className="relative group ml-2">
                <FiInfo 
                  size={16} 
                  className="text-gray-500 hover:text-gray-700 cursor-help transition-colors"
                />
                {/* Tooltip */}
                <div className="absolute top-0 left-full ml-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  Bela is an AI-powered chatbot
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Content Area (Scrollable) */}
        {/* This div now only contains the messages and verification prompt */}
        {/* Added padding-bottom to prevent overlap with input form */}
        <div className="flex-1 overflow-y-auto p-4"> {/* Removed excessive bottom padding - no longer needed with proper flexbox layout */}
          <div className="max-w-4xl mx-auto w-full flex flex-col h-full"> {/* Ensure inner div takes height */}
            {/* Verification Prompt */}
            {!isVerified && (
              <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 rounded-md shadow flex-shrink-0" role="alert">
                <p className="font-bold">Verify Your Email</p>
                <p className="text-sm mb-2">Please check your email inbox (and spam folder) for a verification link. Click the link to enable chat.</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                  <button
                    onClick={handleVerificationCheck}
                    disabled={isCheckingVerification}
                    className="bg-bela-600 hover:bg-bela-700 text-white text-sm font-semibold py-1 px-3 rounded transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {isCheckingVerification ? <><Spinner size="small" color="text-white"/> Checking...</> : 'I have verified my email'}
                  </button>
                  <button
                    onClick={handleResendVerification}
                    disabled={resendStatus.loading}
                    className="text-sm text-bela-700 hover:text-bela-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus.loading ? 'Sending...' : 'Resend verification email'}
                  </button>
                </div>
                 {resendStatus.message && (
                  <p className={`text-sm mt-2 ${resendStatus.error ? 'text-red-600' : 'text-green-600'}`}>
                    {resendStatus.message}
                  </p>
                )}
              </div>
            )}

            {/* Trial Expiry Notice */}
            {isVerified && userInfoLoading && (
              <div className="flex justify-center items-center py-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <Spinner />
                <span className="ml-2 text-blue-700">Verifying subscription status...</span>
              </div>
            )}
            {isVerified && !userInfoLoading && !trialStatus.canUseChat && (
              <TrialExpiredMessage 
                onUpgradeClick={() => setIsSubscriptionModalOpen(true)}
                variant="inline"
                trialDurationDays={trialStatus.trialDurationDays}
              />
            )}

            {/* Message List */}
            {/* Removed flex-grow from here, parent handles layout */}
            <div className={`mb-4 space-y-4 ${!isVerified ? 'opacity-50 pointer-events-none' : ''}`}>
              {isLoadingMessages && isVerified && (
                   <div className="flex justify-center items-center h-full">
                      <Spinner /> <span>Loading messages...</span>
                   </div>
              )}
              {!isLoadingMessages && messages.length === 0 && isVerified && currentConversationId && (
                  <div className="text-center text-gray-500 mt-8">
                      <p>This conversation is empty. Send a message to start!</p>
                  </div>
              )}
               {!isLoadingMessages && messages.length === 0 && isVerified && !currentConversationId && (
                  <div className="text-center text-gray-500 mt-8">
                      <p>Start a new chat with Bela, your AI companion!</p>
                  </div>
              )}
              {!isLoadingMessages && conversationError && isVerified && (
                   <p className="text-red-500 text-center mt-8">{conversationError}</p>
              )}

              {!isLoadingMessages && messages.map((msg, index) => (
                <div
                  key={index} // Consider using message IDs from backend if available and stable
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
                >
                  {/* Bela's avatar - only show for assistant messages */}
                  {msg.role !== 'user' && (
                    <img 
                      src={`${process.env.PUBLIC_URL}/images/bela-avatar-s2.jpeg`}
                      alt="Bela"
                      className="w-10 h-10 rounded-full flex-shrink-0 mt-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setIsAvatarModalOpen(true)}
                      title="Click to view larger image"
                    />
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg p-3 shadow ${
                      msg.role === 'user'
                        ? 'bg-white text-gray-800 rounded-br-none' // User bubble: white bg, dark text
                        : 'bg-rose-50 text-gray-800 rounded-tl-none' // AI bubble: lighter rose bg
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  </div>
                </div>
              ))}

              {isLoading && isVerified && ( // Loading spinner for AI response
                <div className="flex justify-start items-start gap-2">
                  {/* Bela's avatar for typing indicator */}
                  <img 
                    src={`${process.env.PUBLIC_URL}/images/bela-avatar-s2.jpeg`}
                    alt="Bela"
                    className="w-12 h-12 rounded-full flex-shrink-0 mt-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setIsAvatarModalOpen(true)}
                    title="Click to view larger image"
                  />
                  <div className="bg-rose-50 text-gray-800 rounded-lg rounded-tl-none p-3 shadow flex items-center space-x-2 text-sm">
                    <Spinner size="small" />
                    <span>Bela is typing...</span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </div> {/* End Scrollable Chat Content Area */}

        {/* Input Form Area */}
        <div className="bg-gray-100 p-4 border-t border-gray-300 flex-shrink-0">
           <form onSubmit={handleSendMessage} className="flex gap-2 max-w-4xl mx-auto w-full">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat} 
              placeholder={
                !isVerified 
                  ? "Please verify your email to chat"
                  : userInfoLoading
                  ? "Verifying subscription status..."
                  : !trialStatus.canUseChat
                  ? "Trial expired - upgrade to continue chatting"
                  : (currentConversationId ? "Type a message..." : "Start a new conversation...")
              }
              className={`flex-1 p-3 rounded-lg border border-gray-300 focus:outline-none focus:border-bela-500 resize-none min-h-[50px] max-h-[150px] overflow-y-auto text-sm ${
                !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat 
                  ? 'bg-gray-200 cursor-not-allowed' 
                  : 'bg-white'
              }`}
              rows={1}
              style={{ height: 'auto' }} 
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim() || !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat} 
              className="bg-bela-600 hover:bg-bela-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 self-end text-sm"
            >
              Send
            </button>
          </form>
        </div> {/* End Input Form Area */}

      </div> {/* End Main Chat Area */}
      
      {/* Account Modal */}
      <AccountModal 
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        onUpgradeClick={() => {
          setIsAccountModalOpen(false);
          setIsSubscriptionModalOpen(true);
        }}
      />
      
      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        trialStatus={trialStatus}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={isContactSupportModalOpen}
        onClose={() => setIsContactSupportModalOpen(false)}
        firebaseUser={firebaseUser}
      />

      {/* Welcome Modal */}
      <WelcomeModal 
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
        isWaitlistUser={trialStatus.isWaitlistUser}
      />

      {/* Avatar Modal */}
      <AvatarModal 
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
      />
    </div> // End Flex Container
  );
};

export default Chat;
